/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { WorkerRecord } from '@karya/core';
import { parseKaryaIdToken } from './jwt';

/**
 * Handles post-login redirect based on worker registration status.
 * Parses the JWT token from the worker's id_token to check is_registered field.
 * Redirects to /register if not registered, otherwise redirects to /dashboard.
 *
 * @param worker - The worker record containing the id_token
 * @param navigate - React Router navigate function
 */
export function handlePostLoginRedirect(worker: WorkerRecord, navigate: NavigateFunction): void {
  if (!worker.id_token) {
    console.error('No ID token found for worker. Redirecting to dashboard.');
    void navigate('/dashboard', { replace: true });
    return;
  }

  const parseResult = parseKaryaIdToken(worker.id_token);

  if (parseResult.isErr()) {
    console.error('Error parsing ID token:', parseResult.error.prettyPrint());
    // Fallback to homepage on parse error
    void navigate('/', { replace: true });
    return;
  }

  const payload = parseResult.value;
  const isRegistered = payload.is_registered;

  if (isRegistered) {
    void navigate('/dashboard', { replace: true });
  } else {
    void navigate('/register', { replace: true });
  }
}

/**
 * Handles login/dashboard button click.
 * If worker is logged in, redirects to dashboard or register based on registration status.
 * If worker is not logged in, redirects to login page.
 *
 * @param worker - The worker record (can be null if not logged in)
 * @param navigate - React Router navigate function
 */
export function handleLoginOrDashboardClick(worker: WorkerRecord | null, navigate: NavigateFunction): void {
  if (worker) {
    handlePostLoginRedirect(worker, navigate);
  } else {
    void navigate('/login');
  }
}
